import { cleaningActionErrorMessage } from './action-error-copy'
import { useEffect, useRef, useState, type ComponentType, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router'
import { CalendarDays, Check, Copy, Pin, PinOff } from 'lucide-react'
import { api } from '@/api/client'
import type { CleaningJobHistory, CleaningTeam } from '@/api/client'
import { Badge } from '@/components/ui/badge'
import {
  CleaningStatusChip,
  cleaningState,
  isCrewMissing,
} from '@/components/calendar/cleaning-bars'
import { Button } from '@/components/ui/button'
import { DatePicker } from '@/components/ui/date-picker'
import { AckEvidence } from '@/components/cleaning/ack-evidence'
import { CleaningDecideDialog } from '@/components/cleaning/decide-dialog'
import { UndoAction } from '@/components/cleaning/undo-action'
import { applyCleaningJob } from '@/components/cleaning/cache'
import { ErrorState } from '@/components/ui/error-state'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { Spinner } from '@/components/ui/spinner'
import { useToast } from '@/hooks/use-toast'
import {
  CLEANING_SUBSTATE_META,
  DEFAULT_PROPERTY_TIME_ZONE,
  formatCleaningDay,
  formatHistoryActor,
  formatHistoryChange,
  formatHistoryDetail,
  formatHistoryEvent,
  formatMovedFrom,
  isChangeWorthShowing,
  localDate,
  localDateTimeToInstant,
  localTimeOfDay,
  shortTime,
  SOURCE_LABELS,
  tentativeExplanation,
} from '@/components/cleaning/presentation'

/**
 * One cleaning job, opened from a row (CAP-418).
 *
 * This file is the BODY: everything from the property name down. The container
 * it sits in is the caller's (`CleaningJobDrawer`, a centred dialog, on the
 * cleaning page; the side sheet on the property calendar), so one set of
 * actions and one set of rules serves both and cannot drift between them
 * (CAP-875). The container hands in the three pieces of its own chrome the
 * body has to put things in - see `CleaningJobPanelParts`.
 *
 * The board answers "what do I have to do"; this answers "what is this, and
 * what do I want to do about it". Before it, nothing on the board was clickable
 * except the property name, which navigated away - and the only actions were
 * the one-tap buttons a row's own kind offered, so a job could not be given a
 * crew, a note or a different time unless it happened to be sitting in the
 * right group.
 *
 * **Nothing here decides anything.** Which actions are legal, what a substate
 * is called and what the job's own timeline says all arrive from admin-api,
 * from the same functions the digest uses. What lives here is layout and which
 * button is worth offering.
 */

/**
 * The actions this drawer offers, and what a toast says once one lands. `undo` is excluded deliberately: it is not
 * one of the drawer's buttons but the one inside an action's own toast, so it
 * has no label here and never reaches `run`.
 */
type JobAction = Exclude<Parameters<typeof api.runCleaningJobAction>[2], 'undo'>

/**
 * The container's own pieces, which the body renders into.
 *
 * Passed in rather than imported because the title must be the CONTAINER's
 * title component (a dialog's and a sheet's are different Radix contexts, and
 * either one throws or warns without its own), and because only the container
 * knows which part of it scrolls. Each must be a stable reference (declared at
 * module scope), or the body remounts on every render.
 */
/** Substates the status chip does not say: who is still to act, not where the job is. */
const ACK_SUBSTATES: readonly string[] = ['needs_team', 'needs_ack', 'ready']

export interface CleaningJobPanelParts {
  /** The fixed strip the property name sits in. */
  Header: ComponentType<{ children: ReactNode }>
  /** The accessible name of the container: the property name. */
  Title: ComponentType<{ className?: string; children: ReactNode }>
  /** Wraps everything below the header; a fragment where the container scrolls as a whole. */
  Body: ComponentType<{ children: ReactNode }>
}

export interface CleaningJobPanelProps {
  propertyId: string
  jobId: string
  propertyName: string
  teams: CleaningTeam[]
  timeZone: string
  today: string | null
  parts: CleaningJobPanelParts
  /** Called after any action lands, so the list behind can refresh itself. */
  onChanged: () => void
  /**
   * Open the property's month around this job (CAP-514).
   *
   * Handed in rather than rendered here: the calendar is owned by the page, so
   * a row and an open job reach the same one instead of this screen carrying a
   * second copy of it. Given the job's own day, so the calendar opens on the
   * right month without parsing the record again.
   */
  onOpenCalendar?: (day: string | null) => void
}

/**
 * The pair that names this job outside the browser (CAP-520).
 *
 * A job open here could not be referred to anywhere else: both `capehost`
 * commands and any agent working on a cleaning address one by property id and
 * job id, and neither was on screen - so "this one, look at it" meant reading
 * the id out of a network response.
 *
 * The clipboard gets the whole CLI invocation rather than the bare ids: it is
 * what an operator actually wants to run, and it still carries both ids for
 * pasting into a prompt. Shown quietly at the foot of the drawer - a
 * reference, not a control.
 */
function JobReference({ propertyId, jobId }: { propertyId: string; jobId: string }) {
  const [copied, setCopied] = useState(false)
  const { toast } = useToast()
  const command = `capehost cleaning job ${propertyId} ${jobId}`

  return (
    <div className="flex items-center justify-between gap-3 border-t border-border pt-3">
      <code className="min-w-0 truncate font-mono text-xs text-textFaint" title={command}>
        {propertyId} · {jobId}
      </code>
      <Button
        size="sm"
        variant="outline"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(command)
            setCopied(true)
            toast({ title: 'Copied', description: command })
            setTimeout(() => setCopied(false), 2000)
          } catch {
            // A clipboard a browser refuses is not an error worth a red
            // banner: the ids are on screen either way.
            toast({ title: 'Could not copy', description: command })
          }
        }}
      >
        {copied ? (
          <Check className="mr-1.5 h-3.5 w-3.5 text-mint" aria-hidden="true" />
        ) : (
          <Copy className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
        )}
        {copied ? 'Copied' : 'Copy CLI command'}
      </Button>
    </div>
  )
}

/** A labelled line in the details block. */
const CHANNEL_LABELS: Record<string, string> = { vrbo: 'VRBO', airbnb: 'Airbnb' }
const channelLabel = (channel: string) => CHANNEL_LABELS[channel.toLowerCase()] ?? channel

type HistoryEntry = CleaningJobHistory['entries'][number]

/** The changes of an entry worth a line of their own. */
const changesShown = (entry: HistoryEntry) =>
  (entry.changed ?? []).filter(change => isChangeWorthShowing(change, entry.event))

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-3">
      <dt className="w-28 shrink-0 text-xs uppercase tracking-wider text-textFaint">{label}</dt>
      <dd className="text-sm text-text">{children}</dd>
    </div>
  )
}

export function CleaningJobPanel({
  propertyId,
  jobId,
  propertyName,
  teams,
  timeZone,
  today,
  parts: { Header, Title, Body },
  onChanged,
  onOpenCalendar,
}: CleaningJobPanelProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const detailQuery = useQuery({
    queryKey: ['cleaning', 'job', propertyId, jobId],
    queryFn: () => api.getCleaningJob(propertyId, jobId),
  })
  const historyQuery = useQuery({
    queryKey: ['cleaning', 'job-history', propertyId, jobId],
    queryFn: () => api.getCleaningJobHistory(propertyId, jobId),
  })

  const job = detailQuery.data?.job
  const source = detailQuery.data?.source ?? null

  const [teamChoice, setTeamChoice] = useState('')
  const [deciding, setDeciding] = useState(false)

  /**
   * Where the operator wants the cleaning, or null for where it already is.
   *
   * Derived from the job rather than seeded by an effect, for the same reason
   * the note is: an action refetches this job, and copying the stored time
   * into state on every arrival would throw away a date half-picked.
   */
  const [moveDate, setMoveDate] = useState<string | null>(null)
  const [moveTime, setMoveTime] = useState<string | null>(null)

  /**
   * What the operator has typed, or null for "whatever the job says".
   *
   * DERIVED rather than seeded by an effect. An action refetches this job, and
   * an effect copying the stored note into state on every arrival would wipe a
   * half-typed one - so the draft is held only once there is a draft to hold,
   * and cleared on save so the textarea goes back to following the record.
   */
  const [noteDraft, setNoteDraft] = useState<string | null>(null)
  const note = noteDraft ?? job?.note ?? ''
  const noteDirty = noteDraft !== null && noteDraft !== (job?.note ?? '')
  const [noteSavedAt, setNoteSavedAt] = useState<number | null>(null)

  /**
   * The note saves itself (CAP-523).
   *
   * It had a Save button, which is the one control in this drawer that cannot
   * fail in an interesting way and is trivially reversible - so asking for a
   * second click before a typed note counts only bought the chance of closing
   * the drawer on an unsaved one.
   *
   * Its own mutation, not the shared one, on purpose:
   *
   * - the shared `onSuccess` raises a twelve-second toast carrying an Undo,
   *   which is right for "crew assigned" and absurd for every pause in typing;
   * - it also disables the whole drawer through `busy`, which would take the
   *   textarea away mid-sentence.
   *
   * The draft is cleared only once the refetch that carries the saved value
   * has landed AND the text has not moved on since - clearing it on the way
   * out would drop the textarea back to the stored note for as long as the
   * request takes, eating anything typed in between.
   */
  const noteMutation = useMutation({
    mutationFn: (value: string) =>
      api.runCleaningJobAction(propertyId, jobId, 'note', {
        note: value.trim() === '' ? null : value,
      }),
    onSuccess: async (_data, sent) => {
      await queryClient.invalidateQueries({ queryKey: ['cleaning', 'job', propertyId, jobId] })
      await queryClient.invalidateQueries({
        queryKey: ['cleaning', 'job-history', propertyId, jobId],
      })
      setNoteDraft(current => (current === sent ? null : current))
      setNoteSavedAt(Date.now())
      onChanged()
    },
    onError: (error: Error) =>
      toast({
        title: 'That note did not save',
        description: error.message,
        variant: 'destructive',
      }),
  })

  /**
   * Long enough that a save is not fired between two words, short enough that
   * looking away from the keyboard means it is already stored.
   */
  const NOTE_AUTOSAVE_MS = 1200

  // `mutate` is stable across renders, so the debounce below re-arms on the
  // text changing and on nothing else.
  const saveNote = noteMutation.mutate

  useEffect(() => {
    if (!noteDirty) return
    const pending = note
    const timer = setTimeout(() => saveNote(pending), NOTE_AUTOSAVE_MS)
    return () => clearTimeout(timer)
  }, [note, noteDirty, saveNote])

  // "Saved" is about the save that just happened, not a standing claim about
  // the text now in the box, so it goes away on its own.
  useEffect(() => {
    if (noteSavedAt === null) return
    const timer = setTimeout(() => setNoteSavedAt(null), 3000)
    return () => clearTimeout(timer)
  }, [noteSavedAt])

  /**
   * Whatever is still waiting on the debounce, sent now - the panel is going
   * away.
   *
   * Deliberately fires even with a save already in flight. The case that
   * looks like a duplicate is the one that loses text: autosave goes out,
   * another word is typed, the drawer is closed inside the request window -
   * and skipping here would send the operator away with the older text
   * stored, which is the thing this feature exists to prevent. The later
   * request carries the later text.
   *
   * Run on unmount rather than from each container's close handler, so a
   * container that is torn down some other way - the calendar's sheet leaves
   * when the browser's back button drops `open` from the URL - loses nothing
   * either. Reads the latest draft through a ref, since a cleanup closure from
   * the first render would only ever see an empty one.
   */
  const pendingNote = useRef({ dirty: noteDirty, note, save: saveNote })
  useEffect(() => {
    pendingNote.current = { dirty: noteDirty, note, save: saveNote }
  })
  useEffect(
    () => () => {
      const pending = pendingNote.current
      if (pending.dirty) pending.save(pending.note)
    },
    []
  )

  /** A crew's short name, as the board's Crew column and its toasts spell it. */
  const shortCrew = (teamId: string) => teams.find(team => team.id === teamId)?.short_name ?? teamId

  /**
   * Property, day and crew - the same second line the board's toast carries,
   * so a confirmation reads the same wherever the action was taken from.
   *
   * CAP-511 gave the board's toast this line and left the drawer's with only a
   * title, which is the half of the change that was visible in production: an
   * ack from an Upcoming row confirmed itself with no mention of which job it
   * had been.
   */
  const describeJob = (crew?: string) =>
    [
      propertyName,
      job
        ? formatCleaningDay(job.local_date ?? localDate(job.scheduled_at, timeZone), today)
        : null,
      crew ?? (job?.team_id ? shortCrew(job.team_id) : 'No crew'),
    ]
      .filter(Boolean)
      .join(' \u00b7 ')

  const mutation = useMutation({
    mutationFn: ({ action, body }: { action: JobAction; body?: Record<string, unknown> }) =>
      api.runCleaningJobAction(propertyId, jobId, action, body ?? {}),
    onSuccess: (data, variables) => {
      // The crew an assignment is CHOOSING, where there is one: what the
      // response carries is the saved job, but the toast is composed from the
      // request, so an assignment still names the crew being chosen rather
      // than whatever `describeJob` would read off the record.
      const chosen = typeof variables.body?.team_id === 'string' ? variables.body.team_id : null
      const context = describeJob(chosen ? shortCrew(chosen) : undefined)
      toast({
        title: DESCRIBE[variables.action],
        description: context,
        // Long enough to notice a misclick, read what it landed on and reach
        // for Undo - see `UndoAction`.
        duration: 12000,
        action: (
          <UndoAction
            propertyId={propertyId}
            jobId={jobId}
            describe={DESCRIBE[variables.action]}
            context={context}
            onUndone={() => {
              void queryClient.invalidateQueries({
                queryKey: ['cleaning', 'job', propertyId, jobId],
              })
              void queryClient.invalidateQueries({
                queryKey: ['cleaning', 'job-history', propertyId, jobId],
              })
              onChanged()
            }}
          />
        ),
      })
      // Back to following the record: the job written in below is what was
      // saved. Deliberately NOT the note, which has its own mutation now - an
      // assign or a pin landing while a note is half-typed would otherwise
      // wipe the draft AND cancel the autosave that was about to store it,
      // and the pin is one click from the title.
      setMoveDate(null)
      setMoveTime(null)
      // The response IS the saved job, so this drawer and both lists behind it
      // re-render on it at once instead of showing the pre-action state for as
      // long as the refetches take (CAP-525).
      applyCleaningJob(queryClient, data.job)
      // Deliberately not awaited. `isPending` covers whatever this handler
      // returns, so awaiting the refetches held every button in the drawer
      // disabled for a second or two after the work was already done - and the
      // history is the only thing here the response cannot supply.
      void queryClient.invalidateQueries({ queryKey: ['cleaning', 'job', propertyId, jobId] })
      void queryClient.invalidateQueries({
        queryKey: ['cleaning', 'job-history', propertyId, jobId],
      })
      onChanged()
    },
    onError: (error: Error) =>
      toast({
        title: 'That action did not go through',
        description: cleaningActionErrorMessage(error),
        variant: 'destructive',
      }),
  })

  const busy = mutation.isPending
  const run = (action: JobAction, body?: Record<string, unknown>) =>
    mutation.mutate({ action, body })

  /**
   * Where the cleaning currently sits, in the property's own zone, as values
   * the date and time inputs accept - overridden by whatever has been picked.
   */
  const currentDate = moveDate ?? (job ? (localDate(job.scheduled_at, timeZone) ?? '') : '')
  const currentTime = moveTime ?? (job ? (localTimeOfDay(job.scheduled_at, timeZone) ?? '') : '')

  /**
   * The instant those two mean, or null while either is half-typed.
   *
   * Deliberately NOT validated here beyond that. Whether a cleaning may sit at
   * a given time is admin-api's rule and it differs per source - a
   * reservation-sourced job can never be pulled before checkout, a block gets
   * a day of slack, a manual job has no constraint - so a second copy of that
   * switch in the console is exactly the drift this screen's mental model
   * rules out. It posts, and surfaces the refusal.
   */
  const movedInstant = localDateTimeToInstant(currentDate, currentTime, timeZone)

  /**
   * The crews this job can be given to.
   *
   * Active crews, plus whoever it is ALREADY assigned to even if they have
   * since been retired. A retired crew is a real state - it stays the payee on
   * every historical job - so leaving it out of the options made the picker
   * fall back to "Pick a crew" and say the job had nobody, two lines under a
   * Crew field naming them.
   *
   * It is offered so the select can show the truth, not so a retired crew can
   * be newly assigned: picking the crew a job already has is refused below.
   */
  const assignable = teams.filter(team => team.active || team.id === job?.team_id)
  const substate = job?.substate ? CLEANING_SUBSTATE_META[job.substate] : null
  const stillToAct = Boolean(job?.substate && ACK_SUBSTATES.includes(job.substate))
  // A reconcile pass that moved nothing still leaves an "Updated from the
  // booking" event; with no detail and no change it says nothing (CAP-810).
  const shownEntries = (historyQuery.data?.entries ?? []).filter(
    entry => entry.event !== 'job_derived' || entry.detail || changesShown(entry).length > 0
  )
  const tentativeNote = tentativeExplanation(
    job?.confirmation_pending,
    job?.time_zone ?? DEFAULT_PROPERTY_TIME_ZONE
  )
  const crewName = job?.team_id
    ? (teams.find(team => team.id === job.team_id)?.name ?? job.team_id)
    : null

  return (
    <>
      <Header>
        {/* At the top of the drawer, not on the button that was pressed: an
              action can come from any of half a dozen controls further down -
              the pin below included - and the one place an operator is already
              looking is up here.

              The marker sits BESIDE the title rather than inside it:
              the title IS the container's accessible name, so folding
              "Saving" into it renames the dialog mid-action. As its own live
              region it gets announced as what it is - a status - and the name
              stays the property. */}
        <div className="flex items-center gap-2">
          <Title className="text-left">{propertyName}</Title>
          {/* The same icon, word and colour the calendar bar and its legend
              use (CAP-894), so the sheet opened from a bar says what the bar
              said. The State field below keeps the ack detail the bar omits. */}
          {job && (
            <CleaningStatusChip
              state={cleaningState(job)}
              noCrew={isCrewMissing(job)}
              testId="cleaning-panel-status"
            />
          )}
          {/* Pinning says what the OPERATOR wants to keep an eye on, which
                is not what the State section records - everything there is
                something that happened to the cleaning. It sat at the foot of
                the drawer, below the note box, two screens from the job it
                pins (CAP-522). */}
          {job && (
            <Button
              size="icon"
              variant="ghost"
              disabled={busy}
              aria-pressed={Boolean(job.pinned_at)}
              aria-label={job.pinned_at ? 'Unpin this cleaning' : 'Pin this cleaning'}
              title={
                job.pinned_at
                  ? 'Pinned to the top of the board. Click to unpin.'
                  : 'Pin to the top of the board'
              }
              className={job.pinned_at ? 'text-amber hover:text-amber' : 'text-textFaint'}
              onClick={() => run(job.pinned_at ? 'unpin' : 'pin')}
            >
              {job.pinned_at ? (
                <Pin className="h-4 w-4 fill-current" aria-hidden="true" />
              ) : (
                <PinOff className="h-4 w-4" aria-hidden="true" />
              )}
            </Button>
          )}
          <span
            role="status"
            aria-live="polite"
            className="inline-flex shrink-0 items-center gap-1.5 text-xs text-textMuted"
          >
            {busy && (
              <>
                <Spinner />
                Saving
              </>
            )}
          </span>
        </div>
      </Header>
      <Body>
        {/* Right where the call gets made: deciding whether a block needs a
            cleaning is a question about the week, not about this record.

            Gated on the job having arrived, because the month it opens on
            comes from the job: offered any earlier, an eager click lands on
            the current month with nothing called out, and says nothing about
            why. */}
        {onOpenCalendar && job && (
          <div className="-mt-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                onOpenCalendar(job.local_date ?? localDate(job.scheduled_at, timeZone))
              }
            >
              <CalendarDays className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
              See the month around it
            </Button>
          </div>
        )}

        {detailQuery.isLoading ? (
          <div className="space-y-3">
            <LoadingSkeleton className="h-6 w-2/3" />
            <LoadingSkeleton className="h-24 w-full" />
          </div>
        ) : detailQuery.isError || !job ? (
          <ErrorState error={detailQuery.error as Error} onRetry={() => detailQuery.refetch()} />
        ) : (
          <div className="space-y-6">
            {/* --- What this is. */}
            <dl className="space-y-2">
              <Field label="Cleaning">
                <span className="tabular-nums">
                  {formatCleaningDay(
                    job.local_date ?? localDate(job.scheduled_at, timeZone),
                    today
                  )}
                </span>
                <span className="ml-2 tabular-nums text-textMuted">
                  {shortTime(job.scheduled_at, timeZone) ?? '—'}
                </span>
                {/* Names the checkout it left, like the board's row does: a
                    bare "Moved" badge two words from the date it moved TO
                    raises the question it does not answer. */}
                {formatMovedFrom(job.scheduled_at, job.checkout_at, timeZone, today) && (
                  <Badge variant="warning" className="ml-2">
                    {formatMovedFrom(job.scheduled_at, job.checkout_at, timeZone, today)}
                  </Badge>
                )}
              </Field>
              <Field label="Source">
                {SOURCE_LABELS[job.source.kind] ?? job.source.kind}
                {source?.channel && (
                  <span className="ml-2 text-textMuted">via {channelLabel(source.channel)}</span>
                )}
              </Field>
              {source?.guest_name && <Field label="Guest">{source.guest_name}</Field>}
              {source?.check_in && source?.check_out && (
                <Field label="Stay">
                  {/* Spelled the way the board spells a day, not as the raw
                      `YYYY-MM-DD` the API stores. The date part is taken first
                      because a reservation's check-in is sometimes a full
                      datetime, and a calendar date must never be shifted
                      through a timezone. */}
                  <span className="tabular-nums">
                    {formatCleaningDay(source.check_in.slice(0, 10), today)} →{' '}
                    {formatCleaningDay(source.check_out.slice(0, 10), today)}
                  </span>
                  {source.nights !== null && (
                    <span className="ml-2 text-textMuted">
                      {source.nights} {source.nights === 1 ? 'night' : 'nights'}
                    </span>
                  )}
                </Field>
              )}
              {/* The state itself is the chip beside the title - the same icon,
                  word and colour as the calendar bar and its legend. What is
                  left for this row is what the chip does not say: waiting on a
                  crew or an ack (the board's own badge), pinned, and why a
                  tentative job is tentative. */}
              {(stillToAct || job.pinned_at || (job.substate === 'tentative' && tentativeNote)) && (
                <Field label="State">
                  {stillToAct && substate && (
                    <Badge
                      variant={substate.tone as 'warning' | 'success' | 'secondary' | 'destructive'}
                    >
                      {substate.label}
                    </Badge>
                  )}
                  {job.pinned_at && (
                    <Badge variant="secondary" className={stillToAct ? 'ml-2' : undefined}>
                      Pinned
                    </Badge>
                  )}
                  {/* Spelled out here rather than in a tooltip, unlike the board:
                      the drawer has the room, and it is where the Confirm button
                      that overrides the wait sits (CAP-794). */}
                  {job.substate === 'tentative' && tentativeNote && (
                    <p className="mt-1 text-xs text-textMuted">{tentativeNote}</p>
                  )}
                </Field>
              )}
              <Field label="Crew">
                {crewName ?? <span className="text-textFaint">Nobody</span>}
              </Field>
              <Field label="Property">
                <Link
                  to={`/properties/${encodeURIComponent(propertyId)}`}
                  className="text-mint hover:underline"
                >
                  Open {propertyName}
                </Link>
              </Field>
            </dl>

            {job.status !== 'cancelled' && (
              <>
                {/* --- Who does it. Always offered, not only when a row's kind
                happened to be "needs a crew": changing one's mind about the
                crew is the ordinary case, and it had nowhere to happen. */}
                <section className="space-y-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-textFaint">
                    Assign a crew
                  </h3>
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="sr-only" htmlFor="drawer-crew">
                      Crew for {propertyName}
                    </label>
                    <select
                      id="drawer-crew"
                      value={teamChoice || (job.team_id ?? '')}
                      disabled={busy}
                      onChange={event => setTeamChoice(event.target.value)}
                      className="h-8 rounded-input border border-border bg-surface2 px-2 text-sm text-text focus-visible:border-mint focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint disabled:opacity-50"
                    >
                      <option value="">Pick a crew</option>
                      {assignable.map(team => (
                        <option key={team.id} value={team.id}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                    <Button
                      size="sm"
                      disabled={busy || !teamChoice || teamChoice === job.team_id}
                      onClick={() => run('assign', { team_id: teamChoice })}
                    >
                      Assign
                    </Button>
                  </div>
                </section>

                {/* --- When it happens. `scheduled_at` is deliberately separate
                from `checkout_at` so a cleaning can be moved to the next
                morning when nobody arrives that day - and until CAP-424 the
                action that does it had no screen at all. */}
                <section className="space-y-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-textFaint">
                    Move the cleaning
                  </h3>
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="space-y-1">
                      {/* Not a native date input: that can only render
                          10/12/2026, and a crew asks to move a cleaning "to
                          Monday" (CAP-862). */}
                      <span id="drawer-move-date-label" className="block text-xs text-textFaint">
                        Date
                      </span>
                      <DatePicker
                        id="drawer-move-date"
                        labelledBy="drawer-move-date-label"
                        value={currentDate}
                        today={today}
                        disabled={busy}
                        onChange={setMoveDate}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="block text-xs text-textFaint" htmlFor="drawer-move-time">
                        Time
                      </label>
                      <input
                        id="drawer-move-time"
                        type="time"
                        value={currentTime}
                        disabled={busy}
                        onChange={event => setMoveTime(event.target.value)}
                        className="h-8 rounded-input border border-border bg-surface2 px-2 text-sm text-text focus-visible:border-mint focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint disabled:opacity-50"
                      />
                    </div>
                    <Button
                      size="sm"
                      disabled={busy || !movedInstant || movedInstant === job.scheduled_at}
                      onClick={() =>
                        movedInstant && run('reschedule', { scheduled_at: movedInstant })
                      }
                    >
                      Move cleaning
                    </Button>
                  </div>
                  {/* Said before it happens, not discovered afterwards: an
                  operator who does not expect the job to come back marked
                  "Changed" will read that as a bug rather than as the point. */}
                  <p className="text-xs text-textFaint">
                    Moving a cleaning clears the crew&apos;s ack, so they will need to ack it again.
                  </p>
                </section>
              </>
            )}

            {/* --- The note. `job.note` and the `note` action have existed
                since CAP-373 and no screen could set one, so the field was
                unreachable. */}
            <section className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-textFaint">
                Note
              </h3>
              <textarea
                aria-label="Note"
                value={note}
                disabled={busy}
                rows={3}
                onChange={event => setNoteDraft(event.target.value)}
                placeholder="Anything about this cleaning worth remembering"
                className="w-full rounded-input border border-border bg-surface2 px-2 py-1.5 text-sm text-text placeholder:text-textFaint focus-visible:border-mint focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint disabled:opacity-50"
              />
              {/* Says where the note stands rather than asking for a click.
                  "Saved" fades after a moment: a permanent one would read as a
                  claim about the next thing typed. */}
              <p className="text-xs text-textFaint empty:hidden" aria-live="polite">
                {noteMutation.isPending
                  ? 'Saving…'
                  : noteDirty
                    ? 'Saves as you type'
                    : noteSavedAt
                      ? 'Saved'
                      : null}
              </p>
            </section>

            {/* --- State. Only what applies: a control with nothing behind it
                is the thing this workspace's UX rules rule out, and so is a
                heading with nothing under it: a finished cleaning has no
                button here, and the section hides itself. */}
            <section className="space-y-2 has-[div:empty]:hidden">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-textFaint">
                State
              </h3>
              <div className="flex flex-wrap items-center gap-2">
                {job.needs_decision && (
                  <>
                    {/* The SAME dialog the board opens (CAP-419), not a
                        second path. Reading the crew off this drawer's own
                        picker looked equivalent and is not: a job awaiting a
                        decision normally has no crew, so that sent nothing and
                        left the job confirmed with nobody - while the identical
                        decision from the board preselected the property
                        default. Which one you got depended on whether you had
                        clicked the row first. */}
                    <Button size="sm" disabled={busy} onClick={() => setDeciding(true)}>
                      Needs cleaning
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => run('decide', { needs_cleaning: false })}
                    >
                      Not required
                    </Button>
                  </>
                )}
                {job.status === 'scheduled' && !job.needs_decision && !job.confirmed_at && (
                  <Button size="sm" disabled={busy} onClick={() => run('confirm')}>
                    Confirm
                  </Button>
                )}
                {job.status === 'scheduled' && !job.needs_decision && !job.acked_at && (
                  <Button size="sm" disabled={busy} onClick={() => run('ack')}>
                    Record ack
                  </Button>
                )}
                {/* Only for a cleaning that is OFF, which nobody can ack: for
                    one still happening, the crew's ack above is what settles a
                    change, and admin-api refuses "told them" (CAP-530). */}
                {job.status !== 'scheduled' && job.heads_up && !job.heads_up.told_at && (
                  <Button size="sm" disabled={busy} onClick={() => run('told-them')}>
                    Told them
                  </Button>
                )}
                {job.status === 'scheduled' && !job.needs_decision && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => run('not-required')}
                  >
                    Not required
                  </Button>
                )}
                {job.status === 'not_required' && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => run('undo-not-required')}
                  >
                    Undo not required
                  </Button>
                )}
              </div>
            </section>

            {/* --- What has happened to it, newest first. */}
            <section className="space-y-3">
              <div className="flex items-baseline justify-between gap-3">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-textFaint">
                  History
                </h3>
                {historyQuery.data?.source === 'derived' && (
                  // Say it plainly rather than showing "Actor not recorded"
                  // on every line with no explanation of why.
                  <span className="text-xs text-textFaint">
                    Reconstructed - predates change tracking
                  </span>
                )}
              </div>
              {historyQuery.isError ? (
                // A failed timeline is not a failed drawer: everything above
                // still works, so say what is missing rather than replacing
                // the screen with an error.
                <p className="text-sm text-textMuted">
                  The timeline could not be loaded, so what has happened to this cleaning is not
                  shown. Everything above is current.
                </p>
              ) : historyQuery.isLoading ? (
                <LoadingSkeleton className="h-16 w-full" />
              ) : (
                <ol className="relative space-y-4">
                  {shownEntries.map((entry, index, all) => (
                    <li key={`${entry.at}:${entry.event}:${index}`} className="relative flex gap-3">
                      {/* The rail: a dot per entry, joined to the next one.
                          The last entry draws no connector, so the timeline
                          ends rather than trailing off. */}
                      <div className="flex w-3 shrink-0 flex-col items-center pt-1.5">
                        <span
                          className={
                            index === 0
                              ? 'size-2 shrink-0 rounded-full bg-primary'
                              : 'size-2 shrink-0 rounded-full bg-border'
                          }
                        />
                        {index < all.length - 1 && (
                          <span className="mt-1 w-px flex-1 bg-border" aria-hidden />
                        )}
                      </div>
                      <div className="min-w-0 flex-1 pb-1">
                        <div className="flex flex-wrap items-baseline gap-x-2">
                          <span className="text-sm font-medium text-text">
                            {formatHistoryEvent(entry.event)}
                          </span>
                          <span className="text-xs tabular-nums text-textFaint">
                            {formatCleaningDay(localDate(entry.at, timeZone), today)}
                            {shortTime(entry.at, timeZone) && (
                              <> at {shortTime(entry.at, timeZone)}</>
                            )}
                          </span>
                        </div>
                        {entry.detail && (
                          <p className="mt-0.5 text-sm text-textMuted">
                            {formatHistoryDetail(entry.detail, today)}
                          </p>
                        )}
                        {(() => {
                          const shown = changesShown(entry)
                          if (!shown.length) return null
                          return (
                            <ul className="mt-1 space-y-0.5">
                              {shown.map(change => (
                                <li key={change.field} className="text-sm text-textMuted">
                                  {formatHistoryChange(change, timeZone, today)}
                                </li>
                              ))}
                            </ul>
                          )
                        })()}
                        {entry.evidence && (
                          <AckEvidence
                            evidence={entry.evidence}
                            reactedLabel={`${formatCleaningDay(
                              localDate(entry.evidence.reacted_at, timeZone),
                              today
                            )}${
                              shortTime(entry.evidence.reacted_at, timeZone)
                                ? ` at ${shortTime(entry.evidence.reacted_at, timeZone)}`
                                : ''
                            }`}
                          />
                        )}
                        <p className="mt-1 text-xs text-textFaint">
                          {formatHistoryActor(entry.actor)}
                          {entry.actor?.location && (
                            // Coarse and IP-derived, so it is offered as where
                            // the request came from, never as where the person
                            // was standing.
                            <span title="Approximate, from the network the request came over">
                              {' '}
                              · from {entry.actor.location}
                            </span>
                          )}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </section>

            <JobReference propertyId={propertyId} jobId={jobId} />
          </div>
        )}
      </Body>

      {deciding && job && (
        <CleaningDecideDialog
          propertyId={propertyId}
          propertyName={propertyName}
          day={formatCleaningDay(job.local_date ?? localDate(job.scheduled_at, timeZone), today)}
          teams={teams}
          busy={busy}
          onCancel={() => setDeciding(false)}
          onConfirm={teamId => {
            setDeciding(false)
            run('decide', { needs_cleaning: true, ...(teamId ? { team_id: teamId } : {}) })
          }}
        />
      )}
    </>
  )
}

/** What a toast says once an action lands. */
const DESCRIBE: Record<JobAction, string> = {
  assign: 'Crew assigned',
  ack: 'Ack recorded',
  confirm: 'Confirmed',
  'not-required': 'Marked as not required',
  'undo-not-required': 'No longer marked not required',
  decide: 'Decision recorded',
  reschedule: 'Cleaning moved',
  'told-them': 'Marked as told',
  pin: 'Pinned',
  unpin: 'Unpinned',
  note: 'Note saved',
}
